"use client";

import React from 'react';
import { LucideIcon } from 'lucide-react';

interface IconButtonProps {
  children: React.ReactNode;
  icon: LucideIcon;
  variant?: 'primary' | 'secondary' | 'hero' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  iconPosition?: 'left' | 'right';
  onClick?: (e?: React.MouseEvent) => void;
  href?: string;
  target?: string;
  rel?: string;
  preserveCalendlyLink?: boolean;
  disabled?: boolean;
  loading?: boolean;
  ariaLabel?: string;
  type?: 'button' | 'submit' | 'reset';
}

const IconButton: React.FC<IconButtonProps> = ({
  children,
  icon: Icon,
  variant = 'primary',
  size = 'md',
  className = '',
  iconPosition = 'right',
  onClick,
  href,
  target,
  rel,
  preserveCalendlyLink = false,
  disabled = false,
  loading = false,
  ariaLabel,
  type = 'button',
}) => {
  // Base classes for consistent layout
  const baseClasses = `inline-flex items-center justify-center whitespace-nowrap font-semibold transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${className}`;

  // Size variants
  const sizeClasses = {
    sm: 'text-sm py-2 px-4 rounded-lg',
    md: 'text-base py-3 px-6 rounded-xl',
    lg: 'text-lg py-4 px-8 rounded-xl',
  };

  // Variant classes
  const variantClasses = {
    primary: 'btn btn-primary focus:ring-abis-accent/50 focus:ring-offset-abis-primary',
    secondary: 'btn btn-secondary focus:ring-gray-300 focus:ring-offset-white',
    hero: 'btn btn-hero focus:ring-abis-accent/50 focus:ring-offset-abis-primary',
    outline: 'btn btn-outline focus:ring-abis-primary/50 focus:ring-offset-white',
  };

  // Icon classes
  const iconClasses = `${size === 'sm' ? 'w-4 h-4' : size === 'md' ? 'w-5 h-5' : 'w-5 h-5'}`;

  // Gap between icon and text using flexbox gap instead of margins
  const gapClasses = 'gap-2';

  // Combine all classes
  const combinedClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${gapClasses}`.replace(/\s+/g, ' ').trim();

  // Content structure
  const content = (
    <>
      {iconPosition === 'left' && (
        <Icon className={`${iconClasses} ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
      )}
      <span className="whitespace-nowrap flex-shrink-0">{children}</span>
      {iconPosition === 'right' && (
        <Icon className={`${iconClasses} ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
      )}
    </>
  );

  // Handle different element types
  if (href) {
    const isCalendlyLink = href.includes("calendly.com");
    const linkTarget = target || (isCalendlyLink ? "_blank" : undefined);
    const linkRel = rel || (isCalendlyLink ? "nofollow noopener noreferrer" : undefined);

    return (
      <a
        href={href}
        target={linkTarget}
        rel={linkRel}
        data-preserve-calendly={preserveCalendlyLink ? "true" : undefined}
        className={combinedClasses}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={combinedClasses}
      aria-label={ariaLabel}
    >
      {content}
    </button>
  );
};

export default IconButton;
